/**
 * Video — 4 variants (hero_player, gallery, split_intro, testimonial_reel)
 *
 * 動画ソース: Firebase Storage 直配信 / YouTube 埋め込み / Vimeo 埋め込み
 * 未再生時はポスター + 再生ボタン表示。クリックで再生開始。
 */
import { useState } from 'react';

import {
  extractVimeoId,
  extractYouTubeId,
  inferVideoSource,
  type VideoSource,
  vimeoEmbedUrl,
  youtubeEmbedUrl,
  youtubeThumbnail,
} from '../lib/videoEmbed';
import type { VideoContent } from '../schemas/video';
import {
  Body,
  Eyebrow,
  H2,
  H3,
  PlayIcon,
  SectionFrame,
  VisualPlaceholder,
} from './primitives';

interface VP {
  content: VideoContent;
}

interface VideoCardProps {
  poster?: string | null;
  title: string;
  duration?: string | null;
  videoUrl?: string | null;
  videoSource?: VideoSource | null;
  aspectRatio?: string;
}

function VideoCard({
  poster,
  title,
  duration,
  videoUrl,
  videoSource,
  aspectRatio = '16 / 9',
}: VideoCardProps) {
  const [playing, setPlaying] = useState(false);
  const source: VideoSource = videoSource ?? inferVideoSource(videoUrl);

  // YouTube サムネイル自動補完
  let effectivePoster = poster ?? null;
  if (!effectivePoster && source === 'youtube' && videoUrl) {
    const id = extractYouTubeId(videoUrl);
    if (id) effectivePoster = youtubeThumbnail(id);
  }

  if (playing && videoUrl) {
    if (source === 'youtube') {
      const id = extractYouTubeId(videoUrl);
      if (id) {
        return (
          <div
            style={{
              position: 'relative',
              borderRadius: 'var(--ls-radius-lg)',
              overflow: 'hidden',
              aspectRatio,
            }}
          >
            <iframe
              src={`${youtubeEmbedUrl(id)}?autoplay=1&rel=0`}
              title={title}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        );
      }
    } else if (source === 'vimeo') {
      const id = extractVimeoId(videoUrl);
      if (id) {
        return (
          <div
            style={{
              position: 'relative',
              borderRadius: 'var(--ls-radius-lg)',
              overflow: 'hidden',
              aspectRatio,
            }}
          >
            <iframe
              src={`${vimeoEmbedUrl(id)}?autoplay=1`}
              title={title}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
              allow="autoplay; fullscreen; picture-in-picture"
              allowFullScreen
            />
          </div>
        );
      }
    } else {
      // firebase / 直接URL
      return (
        <div
          style={{
            position: 'relative',
            borderRadius: 'var(--ls-radius-lg)',
            overflow: 'hidden',
            aspectRatio,
            background: '#000',
          }}
        >
          <video
            src={videoUrl}
            controls
            autoPlay
            playsInline
            poster={effectivePoster ?? undefined}
            // 公開ページでのダウンロード抑止:
            // - controlsList="nodownload" でブラウザ標準コントロール内の「ダウンロード」を非表示
            // - onContextMenu 抑止で右クリックの「動画を保存」を防止
            // ※ DevTools / Network タブ / 直接 URL アクセスでの取得は防げない (HTML5 video の限界)
            controlsList="nodownload"
            onContextMenu={(e) => e.preventDefault()}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          />
        </div>
      );
    }
  }

  return (
    <button
      type="button"
      onClick={() => videoUrl && setPlaying(true)}
      disabled={!videoUrl}
      aria-label={videoUrl ? `${title} を再生` : title}
      style={{
        position: 'relative',
        display: 'block',
        width: '100%',
        padding: 0,
        border: 0,
        background: 'transparent',
        borderRadius: 'var(--ls-radius-lg)',
        overflow: 'hidden',
        cursor: videoUrl ? 'pointer' : 'default',
      }}
    >
      <VisualPlaceholder src={effectivePoster} aspectRatio={aspectRatio} label={title} />
      {videoUrl && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.2)',
          }}
        >
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.95)',
              color: '#000',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PlayIcon size={28} />
          </div>
        </div>
      )}
      {duration && (
        <span
          style={{
            position: 'absolute',
            bottom: 12,
            right: 12,
            background: 'rgba(0,0,0,0.7)',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: 4,
            fontSize: '0.75rem',
            fontWeight: 500,
          }}
        >
          {duration}
        </span>
      )}
    </button>
  );
}

export function VideoHeroPlayer({ content }: VP) {
  // hero_player は最初の 1 件のみ表示。
  // 各 video の title / description は動画タイトルと説明として表示する。
  const v = content.videos[0];
  if (!v) return null;
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32, alignItems: 'center' }}>
        {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
        {content.headline && <H2 className="ls-h2-center" style={{ textAlign: 'center' }}>{content.headline}</H2>}
        {content.subheadline && (
          <Body style={{ maxWidth: 640, textAlign: 'center' }}>
            {content.subheadline}
          </Body>
        )}
        <div style={{ width: '100%', maxWidth: 1100, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <VideoCard
            poster={v.poster}
            title={v.title}
            duration={v.duration}
            videoUrl={v.videoUrl}
            videoSource={v.videoSource}
            aspectRatio="16 / 9"
          />
          {(v.title || v.description) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, textAlign: 'center' }}>
              {v.title && (
                <H3 style={{ fontSize: '1.25rem' }}>{v.title}</H3>
              )}
              {v.description && (
                <Body style={{ fontSize: '0.95rem', maxWidth: 720, margin: '0 auto' }}>
                  {v.description}
                </Body>
              )}
            </div>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

export function VideoGallery({ content }: VP) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && <H2>{content.headline}</H2>}
          {content.subheadline && (
            <Body style={{ maxWidth: 720 }}>{content.subheadline}</Body>
          )}
        </div>
        <div className="ls-grid ls-grid-2">
          {content.videos.map((v, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <VideoCard
                poster={v.poster}
                title={v.title}
                duration={v.duration}
                videoUrl={v.videoUrl}
                videoSource={v.videoSource}
                aspectRatio="16 / 9"
              />
              <H3 style={{ fontSize: '1.125rem' }}>{v.title}</H3>
              {v.description && <Body style={{ fontSize: '0.9rem' }}>{v.description}</Body>}
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function VideoSplitIntro({ content }: VP) {
  // 左側: 選択中の動画 (デフォルト videos[0]) + タイトル + 説明
  // 右側: section eyebrow / headline / subheadline + 動画一覧（クリックで切替）
  // 一覧には全ての動画を表示し、選択中はハイライト表示。
  const [activeIdx, setActiveIdx] = useState(0);
  const safeIdx = Math.min(Math.max(activeIdx, 0), Math.max(content.videos.length - 1, 0));
  const main = content.videos[safeIdx];
  if (!main) return null;
  return (
    <SectionFrame>
      <div className="ls-split-pair" style={{ gridTemplateColumns: '1.5fr 1fr', alignItems: 'stretch' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* key を変えて VideoCard を再マウントし、内部の playing state をリセット */}
          <VideoCard
            key={safeIdx}
            poster={main.poster}
            title={main.title}
            duration={main.duration}
            videoUrl={main.videoUrl}
            videoSource={main.videoSource}
            aspectRatio="16 / 9"
          />
          {(main.title || main.description) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {main.title && (
                <div style={{ fontWeight: 600, fontSize: '1rem' }}>{main.title}</div>
              )}
              {main.description && (
                <Body style={{ fontSize: '0.9rem' }}>{main.description}</Body>
              )}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && <H3>{content.headline}</H3>}
          {content.subheadline && (
            <Body style={{ fontSize: '0.9rem' }}>{content.subheadline}</Body>
          )}
          {content.videos.length > 1 && (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {content.videos.map((c, i) => {
                const isActive = i === safeIdx;
                return (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => setActiveIdx(i)}
                      style={{
                        width: '100%',
                        padding: 12,
                        border: isActive
                          ? '1px solid var(--ls-accent)'
                          : '1px solid var(--ls-border)',
                        borderRadius: 'var(--ls-radius-md)',
                        background: isActive ? 'var(--ls-accent-soft)' : 'transparent',
                        color: 'inherit',
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                        font: 'inherit',
                        transition: 'background 0.15s, border-color 0.15s',
                      }}
                      aria-current={isActive ? 'true' : undefined}
                      aria-label={`${c.title} を再生`}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontWeight: isActive ? 600 : 500 }}>
                          {isActive && '▶ '}
                          {c.title}
                        </span>
                        {c.duration && (
                          <span style={{ fontSize: '0.75rem', color: 'var(--ls-text-muted)' }}>
                            {c.duration}
                          </span>
                        )}
                      </div>
                      {c.description && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--ls-text-muted)' }}>
                          {c.description}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </SectionFrame>
  );
}

export function VideoTestimonialReel({ content }: VP) {
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          {content.headline && <H2>{content.headline}</H2>}
          {content.subheadline && (
            <Body style={{ maxWidth: 720 }}>{content.subheadline}</Body>
          )}
        </div>
        <div className="ls-grid ls-grid-3">
          {content.videos.slice(0, 3).map((v, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <VideoCard
                poster={v.poster}
                title={v.title}
                duration={v.duration}
                videoUrl={v.videoUrl}
                videoSource={v.videoSource}
                aspectRatio="3 / 4"
              />
              <div style={{ fontWeight: 500 }}>{v.title}</div>
              {v.description && <Body style={{ fontSize: '0.875rem' }}>{v.description}</Body>}
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export const videoVariants = {
  hero_player: VideoHeroPlayer,
  gallery: VideoGallery,
  split_intro: VideoSplitIntro,
  testimonial_reel: VideoTestimonialReel,
};
